import { Injectable } from "@angular/core";
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, NavigationStart, Router, RouterOutlet } from "@angular/router";
import { BehaviorSubject } from "rxjs";
import { RouteHistoryComponent } from "./route-history-component";
import { RouteHistoryItem } from "./route-history-item";
import { RouteHistoryMode } from "./route-history-mode";
import { RouteHistorySubTab } from "./route-history-sub-tab";

@Injectable()
export class RouteHistoryService {
  private _routerOutlets = new Map<RouterOutlet, { mode: RouteHistoryMode }> ();

  history$ = new BehaviorSubject<RouteHistoryItem[]>([]);
  current$ = new BehaviorSubject<RouteHistoryItem>(undefined);
  hasHistory$ = new BehaviorSubject<boolean>(false);
  lastSubTab: RouteHistorySubTab;

  config = {
    historyLength: 20,
    mode: RouteHistoryMode.All
  }

  constructor(private _router: Router,
              private activedRoute: ActivatedRoute) {
    _router.events.subscribe(e => {
      if (e instanceof NavigationStart) {
        this.addCurrentToHistory();
      } else if (e instanceof NavigationEnd) {
        this.setCurrentRoute(activedRoute);
        const outlet = Array.from<RouterOutlet>(
          this._routerOutlets.keys()).find(o => o.isActivated
            && this.getResolvedUrl(o.activatedRoute.snapshot) === this.removeQueryParams(e.url));
        if (outlet) {
          this.setCurrentFromOutlet(outlet);
        }
      }
    });
  }

  private removeQueryParams(url: string): string {
    const index = url.indexOf('?');
    if (index > 0) {
      return url.substring(0, index);
    }
    return url;
  }

  private setCurrentFromOutlet(outlet: RouterOutlet) {
    const outletConfig = this._routerOutlets.get(outlet);
    const displayName = this.getDisplayName(outlet);
    const component = outlet.component;
    if (outletConfig.mode === RouteHistoryMode.All ||
      (outletConfig.mode === RouteHistoryMode.Registered && this.isRouteHistoryComponent(component))) {
      this.current$.next({
        displayName: displayName,
        url: this._router.url,
        route: outlet.activatedRoute,
        outlet: outlet,
        timestamp: new Date()
      });
    }
  }

  private getResolvedUrl(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(v => v.url.map(segment => segment.toString()).join('/'))
      .join('/');
  }

  isRouteHistoryComponent(object: any): object is RouteHistoryComponent {
    if (object.routeHistoryDisplayName instanceof Function) {
      return true;
    }
    return false;
  }

  private getDisplayName(outlet: RouterOutlet): string {
    const component = outlet.component;
    let parentDisplayName;
    if (outlet.activatedRoute.parent && outlet.activatedRoute.parent.routeConfig) {
      const parentOutlet = Array.from<RouterOutlet>(this._routerOutlets.keys())
        .find(o => o.isActivated && this.getResolvedUrl(o.activatedRoute.snapshot) === this.getResolvedUrl(outlet.activatedRoute.parent.snapshot));
      if (!parentOutlet) {
        parentDisplayName = this.getResolvedUrl(outlet.activatedRoute.parent.snapshot);
        if (outlet.activatedRoute.parent.routeConfig.data) {
          parentDisplayName = outlet.activatedRoute.parent.routeConfig.data.displayName;
        }
      } else {
        parentDisplayName = this.getDisplayName(parentOutlet);
      }
    }
    let displayName = this.getResolvedUrl(outlet.activatedRoute.snapshot);
    if (outlet.activatedRoute.routeConfig.data && outlet.activatedRoute.routeConfig.data.displayName) {
      displayName = outlet.activatedRoute.routeConfig.data.displayName;
    } else if (parentDisplayName) {
      displayName = outlet.activatedRoute.routeConfig.path.replace(/([A-Z])/g, ' $1').trim();
      displayName = displayName[0].toUpperCase() + displayName.substring(1);
    }
    if (this.isRouteHistoryComponent(component)) {
      displayName = component.routeHistoryDisplayName();
    }
    if (parentDisplayName) {
      return `${parentDisplayName} - ${displayName}`;
    }
    return displayName;
  }

  registerRouterOutlet(routerOutlet: RouterOutlet, config: { mode: RouteHistoryMode } = { mode: this.config.mode }) {
    this._routerOutlets.set(routerOutlet, config);
    if (routerOutlet.isActivated && this.getResolvedUrl(routerOutlet.activatedRoute.snapshot) === this.removeQueryParams(this._router.url)) {
      this.setCurrentFromOutlet(routerOutlet);
    }
  }

  unRegisterRouterOutlet(routerOutlet: RouterOutlet) {
    if (this._routerOutlets.has(routerOutlet)) {
      this._routerOutlets.delete(routerOutlet);
    }
  }

  setCurrentRoute(route: ActivatedRoute) {
    this.current$.next({ displayName: this._router.url, url: this._router.url, route, timestamp: new Date() });
  }

  private addCurrentToHistory() {
    const current = this.current$.value;
    if (current) {
      if (current.outlet) {
        current.displayName = this.getDisplayName(current.outlet);
      }
      this.history$.next([current, ...this.history$.value].slice(0, this.config.historyLength));
      this.hasHistory$.next(true);
      this.current$.next(undefined);
    }
  }

  goBack(routeHistory?: RouteHistoryItem) {
    const index = routeHistory ? this.history$.value.indexOf(routeHistory) : 0;
    routeHistory = routeHistory || this.history$.value[0];
    if (index >= 0 && routeHistory) {
      this.history$.next(this.history$.value.slice(index + 1));
      if (this.history$.value.length === 0) {
        this.hasHistory$.next(false);
      }
      this.current$.next(undefined);
      this._router.navigateByUrl(routeHistory.url, routeHistory.route.snapshot);
    }
  }

  setLastSubTab(lastTab: RouteHistorySubTab): void{
    this.lastSubTab = lastTab;
  }
}


// WEBPACK FOOTER //
// ./src/app/pillar/layout/route-history.service.ts